// The Flow-k pages' words (7.2 M1): a flow definition (format 1) read out in
// Hungarian — the trigger, the conditions, the waits — and the outline the
// detail page draws: the steps in order, a split's two ways indented under it
// (a multi-branch split's every way, by name, the else marked), and the place
// where the ways meet again drawn once, after them.
//
// Pure functions: tests/admin/test_flows_model.py runs them on Node.

import type {
  FlowCondition,
  FlowDefinition,
  FlowLeaf,
  FlowStep,
  FlowTrigger,
  FlowTriggerDefinition,
  FlowUnsupported,
  FlowWhere,
  FlowWindow,
} from "@/lib/api";

export const COUNTRIES = ["RO", "PL", "HU", "GR", "SK", "EU", "BG", "IT", "CZ", "HR", "SI", "NL"];

export function isUnsupported(value: unknown): value is FlowUnsupported {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { kind?: unknown }).kind === "unsupported"
  );
}

// --- numbers and units ---------------------------------------------------------

// The suffix of "n-szor": by how the number is said. The last non-zero digit
// decides, or for a round number, the word for its tens, hundreds, thousands.
const TIMES_BY_DIGIT: Record<string, string> = {
  "1": "szer",
  "2": "szer",
  "3": "szor",
  "4": "szer",
  "5": "ször",
  "6": "szor",
  "7": "szer",
  "8": "szor",
  "9": "szer",
};
const TIMES_BY_TENS: Record<string, string> = {
  "1": "szer",
  "2": "szor",
  "3": "szor",
  "4": "szer",
  "5": "szer",
  "6": "szor",
  "7": "szer",
  "8": "szor",
  "9": "szer",
};

/** "0-szor", "1-szer", "5-ször", "20-szor", "100-szor", "1000-szer". */
export function times(value: number): string {
  if (!Number.isInteger(value) || value < 0) return `${value}-szer`;
  const digits = String(value);
  const last = digits.at(-1) ?? "0";
  let suffix: string;
  if (value === 0) suffix = "szor";
  else if (last !== "0") suffix = TIMES_BY_DIGIT[last] ?? "szer";
  else if (value % 1000 === 0) suffix = value % 1_000_000 === 0 ? "szor" : "szer";
  else if (value % 100 === 0) suffix = "szor";
  else suffix = TIMES_BY_TENS[digits.at(-2) ?? "1"] ?? "szer";
  return `${value}-${suffix}`;
}

const DELAY_UNIT: Record<string, string> = {
  minutes: "perc",
  hours: "óra",
  days: "nap",
  weeks: "hét",
};

// "az utolsó 7 napban": the unit with its -ban/-ben.
const WINDOW_UNIT_IN: Record<string, string> = {
  hour: "órában",
  day: "napban",
  week: "hétben",
};

const WINDOW_UNIT: Record<string, string> = { hour: "óra", day: "nap", week: "hét" };

const WEEKDAY_ON = ["hétfőn", "kedden", "szerdán", "csütörtökön", "pénteken", "szombaton", "vasárnap"];

/** "08:00" -> "8:00": how a time of day is written in a sentence. */
export function clock(value: string): string {
  const [hour = "", minute = "00", second] = value.split(":");
  const shown = `${Number(hour)}:${minute}`;
  return second && second !== "00" ? `${shown}:${second}` : shown;
}

/** A wait in words: "2 perc", "1 óra", "1 óra 30 perc", "1 nap, aztán 8:00-kor",
 * "5 nap, aztán 18:30-kor, csak hétfőn, szerdán", and a fixed zone's
 * "4 nap, aztán 11:29-kor (Europe/Sofia idő szerint)". */
export function delayText(step: Extract<FlowStep, { kind: "delay" }>): string {
  const unit = DELAY_UNIT[step.unit] ?? step.unit;
  let text = `${step.value} ${unit}`;
  if (step.secondary > 0) {
    text += step.unit === "hours" ? ` ${step.secondary} perc` : ` (+${step.secondary})`;
  }
  if (step.until_time) text += `, aztán ${clock(step.until_time)}-kor`;
  if (step.weekdays && step.weekdays.length > 0) {
    text += `, csak ${step.weekdays.map((day) => WEEKDAY_ON[day] ?? String(day)).join(", ")}`;
  }
  if (step.timezone && step.timezone !== "profile") text += ` (${step.timezone} idő szerint)`;
  return text;
}

/** "valaha", "a flow kezdete óta", "az utolsó 7 napban". */
export function windowText(window: FlowWindow): string {
  if (window.kind === "all_time") return "valaha";
  if (window.kind === "since_flow_start") return "a flow kezdete óta";
  const unit = WINDOW_UNIT_IN[window.unit ?? ""] ?? window.unit ?? "";
  return `az utolsó ${window.quantity ?? "?"} ${unit}`;
}

/** "30 nap": the re-entry wait; null for none, and for "once" and "every
 * time" (`reentrySentence` says those). */
export function reentryText(reentry: FlowDefinition["reentry"]): string | null {
  if (reentry === null) return null;
  if (isUnsupported(reentry)) return "nem támogatott újrabelépési szabály";
  if ("kind" in reentry) return null;
  return `${reentry.quantity} ${WINDOW_UNIT[reentry.unit] ?? reentry.unit}`;
}

/** The re-entry rule as a sentence: "Újra belépni csak 30 nap után lehet.",
 * "Egy kontakt csak egyszer léphet be, soha többé.", "Minden indításkor újra
 * beléphet."; null for none (Klaviyo's default). */
export function reentrySentence(reentry: FlowDefinition["reentry"]): string | null {
  if (reentry === null) return null;
  if (isUnsupported(reentry)) return "Nem támogatott újrabelépési szabály.";
  if ("kind" in reentry) {
    return reentry.kind === "once"
      ? "Egy kontakt csak egyszer léphet be, soha többé."
      : "Minden indításkor újra beléphet (akkor is, ha már volt bent).";
  }
  return `Újra belépni csak ${reentryText(reentry)} után lehet.`;
}

// --- conditions ----------------------------------------------------------------

const SIGN: Record<string, string> = {
  eq: "=",
  ne: "≠",
  gt: ">",
  gte: "≥",
  lt: "<",
  lte: "≤",
  equals: "=",
};

function countText(op: string, value: number): string {
  if (op === "gt" && value === 0) return "legalább egyszer";
  if (op === "eq") return times(value);
  if (op === "ne") return `nem ${times(value)}`;
  if (op === "gt") return `több mint ${times(value)}`;
  if (op === "gte") return `legalább ${times(value)}`;
  if (op === "lt") return `kevesebb mint ${times(value)}`;
  if (op === "lte") return `legfeljebb ${times(value)}`;
  return `${op} ${value}`;
}

function shown(value: unknown, type: string): string {
  return type === "string" ? `„${String(value)}”` : String(value);
}

function whereText(where: FlowWhere): string {
  return `${where.property} ${SIGN[where.op] ?? where.op} ${shown(where.value, where.type)}`;
}

/** One condition in words: "Placed Order 0-szor a flow kezdete óta",
 * "Opened Email legalább egyszer az utolsó 7 napban (ahol $message = „TBCC5y”)",
 * "nem volt ebben a flow-ban az utolsó 30 napban", "FulfillmentHours ≤ 72". */
export function leafText(leaf: FlowLeaf): string {
  if (isUnsupported(leaf)) return "nem támogatott feltétel";
  if (leaf.kind === "metric_count") {
    let text = `${leaf.metric ?? leaf.source_id} ${countText(leaf.op, leaf.value)} ${windowText(leaf.window)}`;
    if (leaf.where.length > 0) text += ` (ahol ${leaf.where.map(whereText).join(", ")})`;
    return text;
  }
  if (leaf.kind === "not_in_flow") return `nem volt ebben a flow-ban ${windowText(leaf.window)}`;
  if (leaf.kind === "sample") return `véletlen ${leaf.percent}%`;
  return `${leaf.property} ${SIGN[leaf.op] ?? leaf.op} ${shown(leaf.value, leaf.type)}`;
}

/** The groups of a condition: every group must hold, inside a group one is
 * enough. An unreadable group or condition is one line saying so. */
export function conditionGroups(condition: FlowCondition): string[][] {
  if (isUnsupported(condition)) return [["nem támogatott feltétel"]];
  return condition.all.map((group) =>
    isUnsupported(group) ? ["nem támogatott feltétel-csoport"] : group.any.map(leafText),
  );
}

/** A condition on one line: groups joined by "és", alternatives by "vagy". */
export function conditionText(condition: FlowCondition | null): string | null {
  if (condition === null) return null;
  return conditionGroups(condition)
    .map((group) => (group.length > 1 ? `(${group.join(" vagy ")})` : (group[0] ?? "")))
    .join(" és ");
}

// --- the trigger ---------------------------------------------------------------

/** What starts the flow, from the list's short form: "Checkout Started",
 * "Lista: Hírlevél", "Szegmens: XxQrFh". */
export function triggerText(trigger: FlowTrigger | null): string {
  if (trigger === null) return "—";
  if (trigger.kind === "metric") {
    const name = trigger.metric ?? trigger.source_id ?? "?";
    return trigger.filtered ? `${name} (szűrve)` : name;
  }
  const name = trigger.source_name ?? trigger.source_id ?? "?";
  if (trigger.kind === "list") return `Lista: ${name}`;
  if (trigger.kind === "segment") return `Szegmens: ${name}`;
  return "nem támogatott indító";
}

/** The trigger in full, from the definition: what, and its filter in words. */
export function triggerSentence(trigger: FlowTriggerDefinition, sourceName: string | null): string {
  if (isUnsupported(trigger)) return "Nem támogatott indító.";
  const name = sourceName ?? trigger.source_id;
  if (trigger.kind === "list") return `Feliratkozik a(z) ${name} listára.`;
  if (trigger.kind === "segment") return `Bekerül a(z) ${name} szegmensbe.`;
  const event = `${trigger.metric ?? trigger.source_id} esemény`;
  const filter = conditionText(trigger.filter);
  return filter ? `${event}, ha ${filter}.` : `${event}.`;
}

/** "50%", "21,4%": an A/B variation's share. */
export function weightText(weight: number): string {
  return `${(weight * 100).toLocaleString("hu-HU", { maximumFractionDigits: 1 })}%`;
}

// --- the outline ---------------------------------------------------------------

export type OutlineNode =
  | { kind: "step"; id: string; step: FlowStep }
  | { kind: "split"; id: string; step: FlowStep; yes: OutlineNode[]; no: OutlineNode[] }
  /** A multi-branch split: its ways in the order they are tried, the else last. */
  | { kind: "branches"; id: string; step: FlowStep; ways: OutlineWay[] }
  /** A way that runs into a step already drawn above. */
  | { kind: "goto"; id: string };

export interface OutlineWay {
  id: string;
  name: string | null;
  isElse: boolean;
  condition: FlowCondition | null;
  nodes: OutlineNode[];
}

export interface Outline {
  nodes: OutlineNode[];
  /** Steps the entry never reaches; drawn apart, so nothing is hidden. */
  unreachable: string[];
}

function linksOf(step: FlowStep | undefined): (string | null | undefined)[] {
  if (step === undefined) return [];
  if (step.kind === "split") return [step.yes, step.no];
  if (step.kind === "multi_split") return [...step.branches.map((each) => each.next), step.otherwise.next];
  if (isUnsupported(step)) return [step.next, step.yes, step.no];
  return [step.next];
}

function isBranch(step: FlowStep): boolean {
  if (step.kind === "split") return true;
  return isUnsupported(step) && ("yes" in step || "no" in step);
}

function reach(steps: Record<string, FlowStep>, start: string | null | undefined): string[] {
  const order: string[] = [];
  const seen = new Set<string>();
  const queue = start ? [start] : [];
  while (queue.length > 0) {
    const id = queue.shift() as string;
    if (seen.has(id) || !(id in steps)) continue;
    seen.add(id);
    order.push(id);
    for (const target of linksOf(steps[id])) if (target) queue.push(target);
  }
  return order;
}

/** Where the ways of a split meet again: the first step all reach that every
 * other shared step comes after. Null when they never all meet. */
function joinOf(steps: Record<string, FlowStep>, ...heads: (string | null | undefined)[]): string | null {
  const [first, ...rest] = heads;
  const others = rest.map((head) => new Set(reach(steps, head)));
  const shared = reach(steps, first).filter((id) => others.every((set) => set.has(id)));
  for (const candidate of shared) {
    const after = new Set(reach(steps, candidate));
    if (shared.every((other) => after.has(other))) return candidate;
  }
  return shared[0] ?? null;
}

/** The steps from the entry, in order: a split's ways under it, the meeting
 * point once after both, a way into a step already drawn as a "goto". */
export function outline(definition: FlowDefinition): Outline {
  const steps = definition.steps;
  const seen = new Set<string>();

  function walk(start: string | null | undefined, stop: Set<string>): OutlineNode[] {
    const nodes: OutlineNode[] = [];
    let current = start ?? null;
    while (current !== null && !stop.has(current)) {
      const step = steps[current];
      if (step === undefined) break;
      if (seen.has(current)) {
        nodes.push({ kind: "goto", id: current });
        break;
      }
      seen.add(current);
      if (step.kind === "multi_split") {
        const ways = [
          ...step.branches.map((branch) => ({ ...branch, isElse: false })),
          { ...step.otherwise, condition: null, isElse: true },
        ];
        const join = joinOf(steps, ...ways.map((way) => way.next));
        const inner = join === null ? stop : new Set([...stop, join]);
        nodes.push({
          kind: "branches",
          id: current,
          step,
          ways: ways.map((way) => ({
            id: way.id,
            name: way.name,
            isElse: way.isElse,
            condition: way.condition,
            nodes: walk(way.next, inner),
          })),
        });
        current = join;
        continue;
      }
      if (isBranch(step)) {
        const ways = step as { yes?: string | null; no?: string | null };
        const join = joinOf(steps, ways.yes, ways.no);
        const inner = join === null ? stop : new Set([...stop, join]);
        nodes.push({
          kind: "split",
          id: current,
          step,
          yes: walk(ways.yes, inner),
          no: walk(ways.no, inner),
        });
        current = join;
        continue;
      }
      nodes.push({ kind: "step", id: current, step });
      current = (step as { next?: string | null }).next ?? null;
    }
    return nodes;
  }

  const entry = typeof definition.entry === "string" ? definition.entry : null;
  const nodes = walk(entry, new Set());
  const unreachable = Object.keys(steps).filter((id) => !seen.has(id));
  return { nodes, unreachable };
}

/** "3 lépés · 2 levél" counted from a definition, as the API counts them. */
export function stepCounts(definition: FlowDefinition): { steps: number; emails: number } {
  const all = Object.values(definition.steps);
  return {
    steps: all.length,
    emails: all.filter((step) => step.kind === "email" || step.kind === "ab").length,
  };
}
